/**
 * @file App.js
 * @author Paola Bustos, Jonas Matulis, Simon Tenedero, Yevheniia Bazhmaieva
 * @created 2024-XX-XX
 * @lastModified 2025-07-09
 * @description Main application component that sets up the routing and state management for the K-Pop Livestream Platform.
 */

import { useState, useEffect, useRef } from "react";
import { Route, Routes, useNavigate, useLocation } from "react-router-dom";
import Header from "./components/Header/Header";
import HomePage from "./pages/HomePage";
import VideoPlayerPage from "./pages/VideoPlayerPage";
import ProfilePage from "./pages/ProfilePage";
import AdminPanel from "./pages/AdminPanelPage";
import SettingsPage from "./pages/SettingsPage";
import { FeatureProvider } from "./context/FeatureContext";
import FeatureRoute from "./routeguard/FeatureRoute";
import ArchivePage from "./pages/ArchivePage";

const App = () => {
  //declaring state variables

  //we want these to retain values between re-mounting (i.e. switching between pages, so we declare them here - this helps with performance)
  const [invitations, setInvitations] = useState([]); //user invitations
  const [privateChats, setPrivateChats] = useState([]); //all private chats user is a member of
  const [selectedPrivateChat, setSelectedPrivateChat] = useState(null); //currently selected private chat
  const [privateChatMembers, setPrivateChatMembers] = useState(new Map()); //private chat members
  const [pendingPrivateChatMembers, setPendingPrivateChatMembers] = useState(
    new Map(),
  ); //pending private chat members
  const [selectedPrivateChatMembers, setSelectedPrivateChatMembers] =
    useState(null); //private chat members of currently selected private chat
  const [
    selectedPrivateChatPendingMembers,
    setSelectedPrivateChatPendingMembers,
  ] = useState(null); //pending private chat members of currently selected private chat
  const [loadingLastPage, setLoadingLastPage] = useState(false);
  const chatCacheRef = useRef(new Map());
  const selectedPrivateChatRef = useRef(null);

  // videoUrl refreshing from a localStorage
  const [videoUrl, setVideoUrl] = useState(
    () => localStorage.getItem("lastVideoUrl") || "",
  );

  const navigate = useNavigate();
  const location = useLocation();

  //detect page reloads, and automatically navigating back to last page
  useEffect(() => {
    const wasReloaded =
      performance.getEntriesByType("navigation")[0]?.type === "reload";

    if (wasReloaded) {
      console.log("reload detected.");
      setLoadingLastPage(true);

      const lastPage = localStorage.getItem("lastPage");
      const browserUrl = window.location.pathname;

      //console.log("lastPage=", lastPage);
      //console.log("browserUrl=", browserUrl);

      // If the user was on /load-live but browser shows /, restore
      if (lastPage && lastPage !== "/" && browserUrl === "/") {
        //console.log("Restoring to:", lastPage);
        navigate(lastPage, { replace: true });
      }

      setLoadingLastPage(false);
    }
  }, []);

  //detecting page changes and keeping it in local storage
  useEffect(() => {
    if (!loadingLastPage) {
      //console.log("page changed to=", location.pathname);
      localStorage.setItem("lastPage", location.pathname);
      //console.log("local storage now has=", localStorage.getItem("lastPage"));
    }
  }, [location.pathname]);

  // for saving videoUrl as it's changing
  useEffect(() => {
    if (videoUrl) {
      localStorage.setItem("lastVideoUrl", videoUrl);
    } else {
      localStorage.removeItem("lastVideoUrl"); // clear the URL and it's ID
    }
  }, [videoUrl]);

  useEffect(() => {
    selectedPrivateChatRef.current = selectedPrivateChat;
  }, [selectedPrivateChat]);

  return (
    <FeatureProvider>
      <div className="app">
        <Header videoUrl={videoUrl} setVideoUrl={setVideoUrl} />
        <div className="content bg-gradient-to-t from-base-300 via-base-200 to-base-100 min-h-screen">
          <Routes>
            <Route exact path="/" element={<HomePage />} />

            <Route
              path="/load-live"
              element={
                <FeatureRoute featureFlag={"livestream_panel"}>
                  <VideoPlayerPage
                    videoUrl={videoUrl}
                    setVideoUrl={setVideoUrl}
                    privateChats={privateChats}
                    setPrivateChats={setPrivateChats}
                    selectedPrivateChat={selectedPrivateChat}
                    setSelectedPrivateChat={setSelectedPrivateChat}
                    privateChatMembers={privateChatMembers}
                    setPrivateChatMembers={setPrivateChatMembers}
                    selectedPrivateChatMembers={selectedPrivateChatMembers}
                    setSelectedPrivateChatMembers={
                      setSelectedPrivateChatMembers
                    }
                    pendingPrivateChatMembers={pendingPrivateChatMembers}
                    setPendingPrivateChatMembers={setPendingPrivateChatMembers}
                    selectedPrivateChatPendingMembers={
                      selectedPrivateChatPendingMembers
                    }
                    setSelectedPrivateChatPendingMembers={
                      setSelectedPrivateChatPendingMembers
                    }
                    invitations={invitations}
                    setInvitations={setInvitations}
                    chatCacheRef={chatCacheRef}
                    selectedPrivateChatRef={selectedPrivateChatRef}
                  />
                </FeatureRoute>
              }
            />

            <Route
              path="/archive"
              element={
                <FeatureRoute featureFlag={"archive_chat"}>
                  <ArchivePage />
                </FeatureRoute>
              }
            />

            <Route
              path="/adminPanel"
              element={
                <FeatureRoute featureFlag={"admin_panel"}>
                  <AdminPanel />
                </FeatureRoute>
              }
            />

            <Route
              path="/profile"
              element={
                <FeatureRoute featureFlag={"profile"}>
                  <ProfilePage />
                </FeatureRoute>
              }
            />

            <Route
              path="/settings"
              element={
                <FeatureRoute featureFlag={"settings"}>
                  <SettingsPage />
                </FeatureRoute>
              }
            />
          </Routes>
        </div>
      </div>
    </FeatureProvider>
  );
};

export default App;
